import axios from "axios";
import { apiEndpoint } from '@iso/lib/helpers/endpoint';
import { authHeader } from '@iso/lib/helpers/authHeader';
import qs from 'qs';

export const educationSessionService = {
  getByActivityPolicy,
  getByEducationAsset,
  getByProperty,
  getTrainees,
  getById,
  storeOrUpdate,
  destroy,
  getQrInfo,
  registerAttendee,
  getAssessment,
  submitAssessment,
  setCompetency,
  setStatus,
  getAssessmentResult,
  setAssessment,
};

/**
 * Get sessions for a property (Training Module)
 */
function getByProperty(propertyId, params = {}) {
  const queryString = qs.stringify({ property_id: propertyId, ...params }, { addQueryPrefix: true });
  return axios
    .get(`${apiEndpoint.educationSessions}/get-by-property${queryString}`, { headers: authHeader() })
    .then(res => {
      if (res && res.status === 200 && res.data.code === '0000') {
        return res.data;
      }
    });
}

/**
 * Get trainees for a session
 */
function getTrainees(sessionId) {
  return axios
    .get(`${apiEndpoint.educationSessions}/get-trainees?session_id=${sessionId}`, { headers: authHeader() })
    .then(res => {
      if (res && res.status === 200 && res.data.code === '0000') {
        return res.data;
      }
    });
}

/**
 * Get education session by activity policy ID
 */
function getByActivityPolicy(activityPolicyId) {
  return axios
    .get(
      `${apiEndpoint.educationSessions}/get-by-activity-policy?activity_policy_id=${activityPolicyId}`,
      {
        headers: authHeader(),
      }
    ).then(res => {
      if (res && res.status === 200 && res.data.code === '0000') {
        return res.data;
      }
    });
}

/**
 * Get education sessions by education asset ID
 */
function getByEducationAsset(educationAssetId) {
  return axios
    .get(
      `${apiEndpoint.educationSessions}/get-by-education-asset?education_asset_id=${educationAssetId}`,
      {
        headers: authHeader(),
      }
    ).then(res => {
      if (res && res.status === 200 && res.data.code === '0000') {
        return res.data;
      }
    });
}

/**
 * Get single education session by ID
 */
function getById(id) {
  return axios
    .get(
      `${apiEndpoint.educationSessions}/view?id=${id}`,
      {
        headers: authHeader(),
      }
    ).then(res => {
      if (res && res.status === 200 && res.data.code === '0000') {
        return res.data;
      }
    });
}

/**
 * Create or update an education session
 */
function storeOrUpdate(data, id = null) {
  return (id ? (
    axios
      .post(
        `${apiEndpoint.educationSessions}/update`,
        { ...data, id },
        {
          headers: authHeader(),
        }
      )
  ) : (
    axios
      .post(
        `${apiEndpoint.educationSessions}/create`,
        data,
        {
          headers: authHeader()
        }
      )
  )).then(res => {
    if (res && res.status === 200) {
      return res.data;
    }
    return {};
  });
}

/**
 * Delete an education session
 */
function destroy(id) {
  return axios
    .post(
      `${apiEndpoint.educationSessions}/delete`,
      { id },
      {
        headers: authHeader()
      }
    ).then(res => {
      if (res && res.status === 200) {
        return res.data;
      }
    });
}

/**
 * Session details for the post-scan confirmation screen, plus whether this
 * session has an assessment and where the caller currently stands on it.
 *
 * Unlike the list calls above, the QR-flow calls resolve with the FULL payload
 * whatever the code is: "register before opening the assessment" and "session
 * not found" are things the attendee needs to read, not silent undefineds.
 */
function getQrInfo(sessionId) {
  return axios
    .get(`${apiEndpoint.educationSessions}/qr-info?session_id=${sessionId}`, { headers: authHeader() })
    .then(res => (res && res.data) || {});
}

/**
 * Add the logged-in user to the session's attendee list. Idempotent — a second
 * scan comes back with already_registered = true rather than an error.
 */
function registerAttendee(sessionId) {
  return axios
    .post(`${apiEndpoint.educationSessions}/register-attendee`, { session_id: sessionId }, { headers: authHeader() })
    .then(res => (res && res.data) || {});
}

/**
 * The assessment form to sit for this session. Requires the caller to already
 * be a registered attendee; the answer key is stripped server-side.
 */
function getAssessment(sessionId) {
  return axios
    .get(`${apiEndpoint.educationSessions}/assessment?session_id=${sessionId}`, { headers: authHeader() })
    .then(res => (res && res.data) || {});
}

/**
 * Submit the assessment answers. Grading happens on the server; the response
 * carries the score and the pass/fail it derived.
 */
function submitAssessment(sessionId, answers) {
  return axios
    .post(
      `${apiEndpoint.educationSessions}/submit-assessment`,
      { session_id: sessionId, answers },
      { headers: authHeader() }
    )
    .then(res => (res && res.data) || {});
}

/**
 * Move a session through draft -> active -> completed.
 *
 * Activating is what publishes the QR code: only an active session accepts
 * self-registration, so this is the call that turns a planned session into one
 * people can scan into. Every transition is reversible — a session completed
 * too early can be reopened.
 */
function setStatus(sessionId, status) {
  return axios
    .post(
      `${apiEndpoint.educationSessions}/set-status`,
      { session_id: sessionId, status },
      { headers: authHeader() }
    )
    .then(res => (res && res.data) || {});
}

/**
 * Trainer override of one attendee's competency. Targets a single attendee
 * rather than re-saving the session, so an override can never disturb the
 * attendee list.
 */
function setCompetency(traineeId, competencyStatus) {
  return axios
    .post(
      `${apiEndpoint.educationSessions}/set-competency`,
      { trainee_id: traineeId, competency_status: competencyStatus },
      { headers: authHeader() }
    )
    .then(res => (res && res.data) || {});
}

/**
 * One attendee's sat assessment, question by question, for marking.
 *
 * Same authority as setCompetency — reading a named person's answers is the
 * input to the override, not a lesser act — so a viewer who cannot record an
 * outcome gets a 403 here too.
 */
function getAssessmentResult(traineeId) {
  return axios
    .get(`${apiEndpoint.educationSessions}/assessment-result`, {
      headers: authHeader(),
      params: { trainee_id: traineeId },
    })
    .then(res => (res && res.data) || {});
}

/**
 * Attach, change or clear the assessment on an existing session.
 *
 * Pass null to clear the session's own override — which means "inherit the
 * training asset's default", not "no assessment". The response reports what the
 * session resolves to afterwards, so the caller never has to work that out.
 */
function setAssessment(sessionId, assessmentFormId) {
  return axios
    .post(
      `${apiEndpoint.educationSessions}/set-assessment`,
      { session_id: sessionId, assessment_form_id: assessmentFormId === undefined ? null : assessmentFormId },
      { headers: authHeader() }
    )
    .then(res => (res && res.data) || {});
}
